import axios from 'axios';

// Défini dans .env.development (local) et .env.production (prod).
const API_URL = process.env.REACT_APP_API_URL || 'http://192.168.1.41:8000/api';

// Configuration axios
const restaurantAPI = axios.create({
  baseURL: `${API_URL}/restaurant`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor pour ajouter le token - CORRIGÉ
restaurantAPI.interceptors.request.use(
  (config) => {
    // CORRECTION : Utiliser 'access_token' au lieu de 'token'
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Ajouter aussi un intercepteur de réponse pour gérer les tokens expirés
restaurantAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Essayer de rafraîchir le token
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) throw new Error('No refresh token');
        
        const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
          refresh: refreshToken
        });
        
        const newToken = response.data.access;
        localStorage.setItem('access_token', newToken);
        
        // Réessayer la requête originale avec le nouveau token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return restaurantAPI(originalRequest);
      } catch (refreshError) {
        // Rediriger vers la page de login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Un envoi contenant un fichier doit partir en multipart ; sinon on reste en
// JSON (l'en-tête par défaut de l'instance).
const configUpload = (data) =>
  (typeof FormData !== 'undefined' && data instanceof FormData)
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : {};

// ===========================
// CATÉGORIES
// ===========================
export const categorieMenuAPI = {
  getAll: () => restaurantAPI.get('/categories-menu/'),
  getById: (id) => restaurantAPI.get(`/categories-menu/${id}/`),
  create: (data) => restaurantAPI.post('/categories-menu/', data),
  update: (id, data) => restaurantAPI.put(`/categories-menu/${id}/`, data),
  // PATCH pour une modification partielle : un PUT exigerait `nom`
  patch: (id, data) => restaurantAPI.patch(`/categories-menu/${id}/`, data),
  delete: (id) => restaurantAPI.delete(`/categories-menu/${id}/`),
};

export const categorieBoissonAPI = {
  getAll: () => restaurantAPI.get('/categories-boissons/'),
  getById: (id) => restaurantAPI.get(`/categories-boissons/${id}/`),
  create: (data) => restaurantAPI.post('/categories-boissons/', data),
  update: (id, data) => restaurantAPI.put(`/categories-boissons/${id}/`, data),
  // PATCH pour une modification partielle : un PUT exigerait `nom`
  patch: (id, data) => restaurantAPI.patch(`/categories-boissons/${id}/`, data),
  delete: (id) => restaurantAPI.delete(`/categories-boissons/${id}/`),
};

// ===========================
// PLATS
// ===========================
export const platAPI = {
  getAll: (params) => restaurantAPI.get('/plats/', { params }),
  getById: (id) => restaurantAPI.get(`/plats/${id}/`),
  // multipart seulement si on passe un FormData (image) — sinon JSON
  create: (data) => restaurantAPI.post('/plats/', data, configUpload(data)),
  update: (id, data) => restaurantAPI.put(`/plats/${id}/`, data, configUpload(data)),
  patch: (id, data) => restaurantAPI.patch(`/plats/${id}/`, data, configUpload(data)),
  delete: (id) => restaurantAPI.delete(`/plats/${id}/`),
  // Menu client : plats disponibles, plats du jour en tête
  menu: (params) => restaurantAPI.get('/plats/menu/', { params }),
};

// ===========================
// BOISSONS
// ===========================
export const boissonAPI = {
  getAll: (params) => restaurantAPI.get('/boissons/', { params }),
  getById: (id) => restaurantAPI.get(`/boissons/${id}/`),
  create: (data) => restaurantAPI.post('/boissons/', data, configUpload(data)),
  update: (id, data) => restaurantAPI.put(`/boissons/${id}/`, data, configUpload(data)),
  // PATCH pour les modifications partielles (bascule de disponibilité, stock) :
  // un PUT exigerait de renvoyer tous les champs obligatoires.
  patch: (id, data) => restaurantAPI.patch(`/boissons/${id}/`, data, configUpload(data)),
  delete: (id) => restaurantAPI.delete(`/boissons/${id}/`),
  menu: (params) => restaurantAPI.get('/boissons/menu/', { params }),
};

// ===========================
// CHAMBRES
// ===========================
export const chambreAPI = {
  getAll: (params) => restaurantAPI.get('/chambres/', { params }),
  getById: (id) => restaurantAPI.get(`/chambres/${id}/`),
  create: (data) => restaurantAPI.post('/chambres/', data, configUpload(data)),
  update: (id, data) => restaurantAPI.put(`/chambres/${id}/`, data, configUpload(data)),
  patch: (id, data) => restaurantAPI.patch(`/chambres/${id}/`, data, configUpload(data)),
  delete: (id) => restaurantAPI.delete(`/chambres/${id}/`),
  getAvailable: (params) => restaurantAPI.get('/chambres/disponibles/', { params }),
  // Plan de la réception : chambres + statut + location en cours
  plan: () => restaurantAPI.get('/chambres/plan/'),
  marquerDisponible: (id) => restaurantAPI.post(`/chambres/${id}/marquer_disponible/`),
};

// ===========================
// LOCATIONS
// ===========================
export const locationAPI = {
  getAll: (params) => restaurantAPI.get('/locations/', { params }),
  getById: (id) => restaurantAPI.get(`/locations/${id}/`),
  // Le check-in EST la création : le serveur pose heure_debut et heure_fin_prevue
  create: (data) => restaurantAPI.post('/locations/', data),
  checkOut: (id) => restaurantAPI.post(`/locations/${id}/check_out/`),
  payer: (id, montant) => restaurantAPI.post(`/locations/${id}/payer/`, { montant }),
  update: (id, data) => restaurantAPI.put(`/locations/${id}/`, data),
  getActive: () => restaurantAPI.get('/locations/en_cours/'),
};

// ===========================
// COMMANDES
// ===========================
export const commandeAPI = {
  getAll: (params) => restaurantAPI.get('/commandes/', { params }),
  getById: (id) => restaurantAPI.get(`/commandes/${id}/`),
  create: (data) => restaurantAPI.post('/commandes/', data),
  update: (id, data) => restaurantAPI.put(`/commandes/${id}/`, data),
  delete: (id) => restaurantAPI.delete(`/commandes/${id}/`),
  // `remise` est optionnelle et rejouable ; envoyer 0 l'annule
  demanderAddition: (id, remise = 0) => restaurantAPI.post(`/commandes/${id}/demander_addition/`, { remise }),
  // `montant_recu` est facultatif : omis = encaissement du montant exact
  payer: (id, data = {}) => restaurantAPI.post(`/commandes/${id}/payer/`, data),
  annuler: (id, motif = '') => restaurantAPI.post(`/commandes/${id}/annuler/`, { motif }),
  addItems: (id, data) => restaurantAPI.post(`/commandes/${id}/ajouter_lignes/`, data),
  supprimerLigne: (id, ligneId) => restaurantAPI.delete(`/commandes/${id}/supprimer_ligne/${ligneId}/`),
  modifierLigne: (id, ligneId, data) => restaurantAPI.patch(`/commandes/${id}/modifier_ligne/${ligneId}/`, data),
  venteDirecte: (data) => restaurantAPI.post('/commandes/vente_directe/', data),
  getActive: () => restaurantAPI.get('/commandes/ouvertes/'),
  getByChambre: (params) => restaurantAPI.get('/commandes/par_chambre/', { params }),
};

// ===========================
// EMPLOYÉS
// ===========================
export const employeAPI = {
  getAll: () => restaurantAPI.get('/employes/'),
  getById: (id) => restaurantAPI.get(`/employes/${id}/`),
  create: (data) => restaurantAPI.post('/employes/', data),
  update: (id, data) => restaurantAPI.put(`/employes/${id}/`, data),
  delete: (id) => restaurantAPI.delete(`/employes/${id}/`),
  actifs: () => restaurantAPI.get('/employes/actifs/'),
};

// ===========================
// DÉPENSES
// ===========================
export const depenseAPI = {
  getAll: (params) => restaurantAPI.get('/depenses/', { params }),
  getById: (id) => restaurantAPI.get(`/depenses/${id}/`),
  create: (data) => restaurantAPI.post('/depenses/', data),
  update: (id, data) => restaurantAPI.put(`/depenses/${id}/`, data),
  delete: (id) => restaurantAPI.delete(`/depenses/${id}/`),
  // params: { debut: 'YYYY-MM-DD', fin: 'YYYY-MM-DD' }
  getStats: (params) => restaurantAPI.get('/depenses/par_periode/', { params }),
};

// ===========================
// DASHBOARD
// ===========================
// Un seul appel renvoie tout : aujourd_hui, temps_reel, ce_mois, hotel,
// depenses, top_ventes, finances_visibles.
export const dashboardAPI = {
  get: () => restaurantAPI.get('/dashboard/'),
};

// ===========================
// OPTIONS DE PLATS
// ===========================
// Une option est rattachée à un plat via le champ `plat`.
export const optionPlatAPI = {
  getAll: (params) => restaurantAPI.get('/options-plats/', { params }),
  getById: (id) => restaurantAPI.get(`/options-plats/${id}/`),
  create: (data) => restaurantAPI.post('/options-plats/', data),
  update: (id, data) => restaurantAPI.put(`/options-plats/${id}/`, data),
  patch: (id, data) => restaurantAPI.patch(`/options-plats/${id}/`, data),
  delete: (id) => restaurantAPI.delete(`/options-plats/${id}/`),
};

// ===========================
// RÉSERVATIONS
// ===========================
export const reservationAPI = {
  getAll: (params) => restaurantAPI.get('/reservations/', { params }),
  getById: (id) => restaurantAPI.get(`/reservations/${id}/`),
  create: (data) => restaurantAPI.post('/reservations/', data),
  update: (id, data) => restaurantAPI.put(`/reservations/${id}/`, data),
  delete: (id) => restaurantAPI.delete(`/reservations/${id}/`),
  // Pas de `confirm` : une réservation créée est déjà 'confirmee'.
  cancel: (id) => restaurantAPI.post(`/reservations/${id}/annuler/`),
  // Convertit la réservation en location et occupe la chambre
  checkIn: (id) => restaurantAPI.post(`/reservations/${id}/check_in/`),
};
export default restaurantAPI;